import * as THREE from 'three';

/**
 * Renderer ownership: device setup, resize, and the frame's draw order.
 *
 * Two structural decisions worth stating up front.
 *
 * 1. The viewmodel renders in a SEPARATE scene with its own camera and a narrow
 *    FOV, drawn after the world with the depth buffer cleared. A first-person
 *    weapon held ~40 cm from the eye would otherwise clip through every wall the
 *    player stands near, and matching the world FOV would distort it badly at
 *    the 95 degrees this game uses.
 *
 * 2. Post-processing renders into an HDR float target and resolves once at the
 *    end. Bloom and tonemapping are meaningless on values already clamped to
 *    [0,1] — the bright parts have to actually be bright for any of it to work.
 */
export class Renderer {
  constructor(ctx) {
    this.ctx = ctx;
    const cfg = ctx.config;

    const renderer = new THREE.WebGLRenderer({
      canvas: ctx.canvas,
      antialias: false,          // FXAA runs in the post chain instead
      alpha: false,
      stencil: false,
      depth: true,
      powerPreference: 'high-performance',
      // Needed so the screenshot harness can read the canvas after a frame.
      preserveDrawingBuffer: cfg.capture === true,
    });

    renderer.debug.checkShaderErrors = cfg.dev === true;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.NoToneMapping; // the post chain owns tonemapping
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.shadowMap.autoUpdate = true;
    renderer.autoClear = false;
    renderer.setClearColor(0x05070b, 1);

    // A frame issues several render() calls (world, viewmodel, then every post
    // blit). Three resets its stats at the start of each one, so left on auto
    // the counters would only ever report the final full-screen blit. Reset
    // manually once per frame instead so the dev overlay shows real totals.
    renderer.info.autoReset = false;

    this.renderer = renderer;
    this.maxAnisotropy = Math.min(
      cfg.q.anisotropy,
      renderer.capabilities.getMaxAnisotropy()
    );

    this.width = 1;
    this.height = 1;
    this.pixelRatio = 1;

    /** @type {THREE.WebGLRenderTarget|null} */
    this.hdr = null;
    /** @type {import('./post/composer.js').Composer|null} */
    this.composer = null;

    this._onResize = () => this.resize();
    window.addEventListener('resize', this._onResize);
    this.resize();
  }

  /**
   * Attach the post chain. Registered separately so the renderer can boot and
   * draw before the composer exists.
   *
   * The composer must be sized HERE rather than relying on a later resize()
   * call: resize() early-returns when the window has not actually changed, so a
   * composer attached after the first resize would keep its placeholder 1x1
   * targets and blit a single flat pixel to the screen.
   */
  setComposer(composer) {
    this.composer = composer;
    composer.resize(
      Math.max(1, Math.floor(this.width * this.pixelRatio)),
      Math.max(1, Math.floor(this.height * this.pixelRatio))
    );
  }

  resize() {
    const cfg = this.ctx.config;
    const dpr = Math.min(window.devicePixelRatio || 1, cfg.q.pixelRatioCap);
    const w = Math.max(1, window.innerWidth);
    const h = Math.max(1, window.innerHeight);

    if (w === this.width && h === this.height && dpr === this.pixelRatio) return;

    this.width = w;
    this.height = h;
    this.pixelRatio = dpr;

    this.renderer.setPixelRatio(dpr);
    this.renderer.setSize(w, h, false);

    const bw = Math.max(1, Math.floor(w * dpr));
    const bh = Math.max(1, Math.floor(h * dpr));

    if (!this.hdr) {
      this.hdr = new THREE.WebGLRenderTarget(bw, bh, {
        type: THREE.HalfFloatType,
        format: THREE.RGBAFormat,
        colorSpace: THREE.LinearSRGBColorSpace,
        depthBuffer: true,
        stencilBuffer: false,
        samples: 0,
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
      });
      this.hdr.texture.name = 'hdr';
    } else {
      this.hdr.setSize(bw, bh);
    }

    const cam = this.ctx.camera;
    if (cam) {
      cam.aspect = w / h;
      cam.updateProjectionMatrix();
    }
    const vcam = this.ctx.viewCamera;
    if (vcam) {
      vcam.aspect = w / h;
      vcam.updateProjectionMatrix();
    }

    this.composer?.resize(bw, bh);
    this.ctx.events.emit('render:resize', { width: w, height: h, dpr, bw, bh });
  }

  /**
   * Draw order: world into HDR, viewmodel on top with depth cleared, then the
   * post chain resolves HDR to the canvas.
   */
  render() {
    const { renderer, ctx } = this;
    const target = this.composer ? this.hdr : null;

    renderer.info.reset();

    renderer.setRenderTarget(target);
    renderer.clear(true, true, false);
    renderer.render(ctx.scene, ctx.camera);

    if (ctx.viewScene && ctx.viewScene.children.length > 0) {
      // Clear depth only. The weapon must sit in front of everything without
      // erasing the world colour already drawn.
      renderer.clearDepth();
      renderer.render(ctx.viewScene, ctx.viewCamera);
    }

    if (this.composer) {
      renderer.setRenderTarget(null);
      this.composer.render(this.hdr);
    }
  }

  get info() {
    return this.renderer.info;
  }

  dispose() {
    window.removeEventListener('resize', this._onResize);
    this.hdr?.dispose();
    this.composer?.dispose();
    this.renderer.dispose();
  }
}
